import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

import { FieldInterventionLayerBloc } from '@connect-services/client/blocs/field-intervention-layer.bloc';
import { LayerDataAttributesListComponent } from '@connect-services/client/components/field-intervention-layer-detail/layer-data-attributes-list/layer-data-attributes-list.component';
import { TitleComponent } from '@connect-services/client/components/field-intervention-title/title/title.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { excludedAttributes, labelByLayerType } from '@connect-services/client/services/layer.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { LayerAttribute, LayerWithAttributes } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, LayerDataAttributesListComponent, LoadingComponent, RessourcesModule, TitleComponent],
    selector: 'app-field-intervention-layer-detail',
    styleUrl: './field-intervention-layer-detail.component.scss',
    templateUrl: './field-intervention-layer-detail.component.html',
})
export class FieldInterventionLayerDetailComponent {
    protected readonly attributesBookmarked: Signal<LayerAttribute[] | undefined> = computed(() =>
        this.layer()?.dataAttributes.filter((dataAttribute: LayerAttribute) => dataAttribute.isBookmarked),
    );
    protected readonly attributesOther: Signal<LayerAttribute[] | undefined> = computed(() =>
        this.layer()?.dataAttributes.filter((dataAttribute: LayerAttribute) => !dataAttribute.isBookmarked),
    );
    protected readonly ButtonType = ButtonType;
    protected readonly currentPanelEntity: WritableSignal<string | null> = signal(null);
    protected readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    protected readonly fieldInterventionService = inject(FieldInterventionService);
    protected readonly isLoading = signal(true);
    protected readonly layer: WritableSignal<LayerWithAttributes | null> = signal(null);
    protected readonly layerType = computed(() => {
        const keyToTranslate = labelByLayerType[this.layer()!.type];

        return this.translocoService.translate(keyToTranslate, { COUNT: 1 });
    });
    protected readonly Object = Object;
    protected readonly RoutePathEnum = RoutePathEnum;
    private activatedRoute = inject(ActivatedRoute);
    private readonly fieldInterventionLayerBloc = inject(FieldInterventionLayerBloc);
    private readonly router = inject(Router);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl());

        this.initializeLayer();

        this.router.events
            .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((): void => {
                this.currentPanelEntity.set(this.fieldInterventionService.getDetailPanelUuidFromUrl());

                this.initializeLayer();
            });
    }

    public async goToEntitiesTable(): Promise<void> {
        await this.router.navigate(
            [
                `../../${RoutePathEnum.FIELD_INTERVENTION_ENTITIES}`,
                {
                    outlets: { detailPanel: null },
                },
            ],
            {
                queryParams: { selectedLayer: this.layer()!.name },
                queryParamsHandling: 'merge',
                relativeTo: this.activatedRoute,
            },
        );
    }

    private initializeLayer(): void {
        const layerId = this.fieldInterventionService.getDetailPanelUuidFromUrl();

        if (!layerId) {
            return;
        }
        this.setLayer(layerId);
        this.setDataAttributes();
    }

    private setDataAttributes(): void {
        const sortedAttributes = this.layer()!.dataAttributes.sort((a, b) => a.column.localeCompare(b.column));

        this.layer()!.dataAttributes = sortedAttributes.filter(
            (attribute: LayerAttribute) => !excludedAttributes.includes(attribute.column),
        );
    }

    private setLayer(layerId: string): void {
        const layer = this.fieldInterventionLayerBloc
            .value()!
            .items.find((layer: LayerWithAttributes) => layer.id === layerId);

        if (!layer) {
            return;
        }

        this.layer.set(layer);
    }
}
